/**
 * The hooks and components in this file provide consistent/standardized ways for users to manage files in a form that corresponds to some entity (for example, the files attached to an issue or exception).
 * Terminology: A "file management area" is an area of the page where the user can upload new files and view/download/delete existing files. It uses the `FileDragAndDrop` component under the hood, which may or may not be in "single file" mode.
 *
 * The file state for a file management area is managed by either the `useFileManagementArea` hook or the `useFileManagementAreas` hook. The area is rendered by either the `FileManagementArea` component or the `FileManagementAreaWithSiblings` component.
 *
 * How to choose the right hook and component(s) to use:
 * * The entity has a single set of files associated with it (issues, exceptions, etc.): Use `useFileManagementArea` and `FileManagementArea`.
 * * The entity has multiple sets/sections of files associated with it (IRQ with multiple sections, a control in a third-party due diligence assessment with multiple questions, etc.): Use `useFileManagementAreas` and one instance of `FileManagementAreaWithSiblings` per independent section.
 *
 * NOTE: IDs for file drag-and-drop inputs are generated automatically. Do not use an ID equal to or beginning with `fileManagementArea` for any element on the page.
 *
 * TODO: File validation should occur when dropping in new files, not during the request submission process.
 */
import ListItemIcon from '@mui/material/ListItemIcon';
import { AxiosProgressEvent } from 'axios';
import { type JSX, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { IconButton } from 'Components/Buttons/IconButton';
import { OverflowItem, OverflowMenu } from 'Components/Buttons/OverflowMenu';
import { Colors } from 'Components/Colors';
import { IndicatorVariant } from 'Components/Indicator/Indicator';
import { ProgressBarIndicator } from 'Components/Indicator/ProgressBarIndicator';
import { MUITable, MUITableBody, MUITableCell, MUITableOverflowCell, MUITableRow } from 'Components/MUITable/MUITable';
import { Text } from 'Components/Text/Text';
import { FileStateToolTip } from 'Components/Tooltips/FileStateToolTip';
import { VisualLabel } from 'Components/VisualLabel/VisualLabel';
import { ICON_DELETE_REMOVE, ICON_DOWNLOAD } from 'Config/Icons';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { downloadDocument } from 'Helpers/FileUtils';
import { validateFileForUpload } from 'Helpers/InputValidation';
import { FileState, FileToBeUploaded, FileUpdates, SignedUploadResponse, UploadedFile } from 'Models/Files';

import styles from './FileManagementArea.module.css';

export const DEFAULT_LABEL_FOR_FILE_DRAG_AND_DROP_SINGLE_MODE = 'File';
export const DEFAULT_LABEL_FOR_EXISTING_FILES_SINGLE_MODE = 'Existing File';
export const DEFAULT_LABEL_FOR_FILE_DRAG_AND_DROP_MULTIPLE_MODE = 'Files';
export const DEFAULT_LABEL_FOR_EXISTING_FILES_MULTIPLE_MODE = 'Existing Files';

/** A default unique ID for a single file management area on the page. */
const FILE_MANAGEMENT_AREA_DRAG_AND_DROP_INPUT_ID = 'fileManagementArea';

/** Functions similarly to Python's `defaultdict`, such that `get` will always return a value. Used to facilitate cleaner code when looking up file management state for pages that render multiple file management areas. */
class DefaultMap<K, V> extends Map<K, V> {
    generateDefaultValue: () => V;

    constructor(generateDefaultValue: () => V, entries?: [K, V][]) {
        super(entries);
        this.generateDefaultValue = generateDefaultValue;
    }

    get(key: K): V {
        if (!this.has(key)) {
            const defaultValue = this.generateDefaultValue();
            this.set(key, defaultValue);
            return defaultValue;
        }
        return super.get(key)!;
    }

    copy(): DefaultMap<K, V> {
        return new DefaultMap(this.generateDefaultValue, [...this.entries()]);
    }
}

enum UploadProgressOverallStatus {
    NOT_STARTED = 'Not Started',
    IN_PROGRESS = 'In Progress',
    COMPLETE = 'Complete',
}

/**
 * Information tracked as part of file uploads for displaying to the user.
 * Possible future improvement: create a wrapper for the `File` type that has these attributes on a per-file basis so we can show progress of individual file uploads.
 */
interface UploadProgress {
    currentFileUploadPercent: number;
    filesToUploadCount: number;
    fileUploadsCompleteCount: number;
    overallStatus: UploadProgressOverallStatus;
}

type ManagedFilesForArea = { existingFiles: UploadedFile[]; existingFilesToDelete: string[]; newFiles: File[]; uploadProgress: UploadProgress };

export enum FileManagementAreaRestriction {
    NO_RESTRICTION,
    /**
     * An error will be thrown if:
     * * the user submits the form with neither an existing file nor a new file.
     * * the user submits the form with multiple (new or existing) files. This _should_ be impossible, since `FileDragAndDropSingleMode` prevents multiple files from being selected.
     * The file drag-and-drop input will enforce only a single file being associated with the entity. Look at `FileDragAndDrop` vs. `FileDragAndDropSingleMode` for more details.
     */
    EXACTLY_ONE_FILE,
    /**
     * An error will be thrown if the user submits the form with multiple (new or existing) files. This _should_ be impossible, since `FileDragAndDropSingleMode` prevents multiple files from being selected.
     * The file drag-and-drop input will enforce only a single file being associated with the entity. Look at `FileDragAndDrop` vs. `FileDragAndDropSingleMode` for more details.
     */
    UP_TO_ONE_FILE,
}

/**
 * Contains current file state for a `FileManagementArea`.
 * You should not need to use anything from an object that conforms to this interface. Just pass the object directly to `FileManagementArea`.
 */
export interface FileManagementAreaHookValues {
    existingFiles: UploadedFile[];
    newFiles: File[];
    onAddNewFiles: (files: File[]) => void;
    onRemoveNewFile: (file: File) => void;
    onRemoveExistingFile: (file: UploadedFile) => void;
    restriction: FileManagementAreaRestriction;
    uploadProgress: UploadProgress;
}

/**
 * Provides logic for a `FileManagementArea` that manages files associated with some entity (an issue, exception, etc.).
 * NOTE: You usually don't need to specify `SubmitApiRequestReturnType` (if you don't define it, it will default to `unknown`). Define it when the request for submitting a form returns a response that you need to make use of.
 *
 * @param documentApi The API used to upload files to S3 when the form is submitted.
 * @param existingFilesDefault The files currently associated with the entity at page load time.
 * @param restriction The restriction on the number of files that can be associated with the entity. Defaults to `FileManagementAreaRestriction.NO_RESTRICTION`.
 * @returns Values to be passed directly into the corresponding `FileManagementArea`, as well as a function that handles the "S3" dance when it comes time to submit the form, a boolean indicating whether there are any unsaved changes, and a function that discards any unsaved changes (reverting to the last-saved set of files). The boolean is intended to be used in conjunction with `useUnsavedChangesTracking`, if a form needs it. The discard function is useful when a form offers the user a way to abandon unsaved changes.
 */
export const useFileManagementArea = <SubmitApiRequestReturnType,>(documentApi: DocumentApi, existingFilesDefault: UploadedFile[], restriction?: FileManagementAreaRestriction): readonly [(submitApiRequest: (fileUpdates: FileUpdates) => Promise<SubmitApiRequestReturnType>) => Promise<SubmitApiRequestReturnType>, FileManagementAreaHookValues, boolean, (newExistingFiles?: UploadedFile[]) => void] => {
    const [managedFiles, setManagedFiles] = useState<ManagedFilesForArea>({
        existingFiles: existingFilesDefault,
        existingFilesToDelete: [],
        newFiles: [],
        uploadProgress: {
            currentFileUploadPercent: 0,
            filesToUploadCount: 0,
            fileUploadsCompleteCount: 0,
            overallStatus: UploadProgressOverallStatus.NOT_STARTED,
        },
    });

    /** The set of existing files as of the last successful save (or page load). Used to restore state when unsaved changes are discarded. */
    const [lastSavedExistingFiles, setLastSavedExistingFiles] = useState<UploadedFile[]>(existingFilesDefault);

    const _restriction = restriction ?? FileManagementAreaRestriction.NO_RESTRICTION;

    const onAddNewFiles = (newFiles: File[]) => {
        setManagedFiles((current) => {
            const currentFileNames = [...current.newFiles.map((file) => file.name), ...current.existingFiles.map((file) => file.filename)];
            const validNewFiles = newFiles.filter((file) => !currentFileNames.includes(file.name));
            const sortedValidFiles = [...validNewFiles, ...current.newFiles].sort((a, b) => {
                return a.name.localeCompare(b.name);
            });

            return { ...current, newFiles: sortedValidFiles };
        });
    };

    const onRemoveNewFile = (fileToRemove: File) => {
        setManagedFiles((current) => ({ ...current, newFiles: current.newFiles.filter((file) => file.name !== fileToRemove.name) }));
    };

    const onRemoveExistingFile = (fileToRemove: UploadedFile) => {
        setManagedFiles((current) => ({ ...current, existingFiles: current.existingFiles.filter((file) => file.file_id !== fileToRemove.file_id), existingFilesToDelete: [...current.existingFilesToDelete, fileToRemove.file_id] }));
    };

    /** Callback function that is executed when a file upload is complete. */
    const onIndividualUploadComplete = (unusedFileManagementAreaId: string, fileUploadsCompleteCount: number) => {
        // Setting currentFileUploadPercent to 0 when a file upload completes ensures the upload progress bar in the UI doesn't jump around.
        setManagedFiles((current) => ({ ...current, uploadProgress: { ...current.uploadProgress, currentFileUploadPercent: 0, fileUploadsCompleteCount: fileUploadsCompleteCount } }));
    };

    /** Callback function that is executed as a file is being uploaded. */
    const onIndividualUploadProgress = (unusedFileManagementAreaId: string, progressEvent: AxiosProgressEvent) => {
        if (progressEvent.total) {
            const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setManagedFiles((current) => ({ ...current, uploadProgress: { ...current.uploadProgress, currentFileUploadPercent: percentComplete } }));
        }
    };

    /** Callback function that is executed once all file uploads are complete. */
    const onOverallUploadComplete = (unusedFileManagementAreaId: string) => {
        setManagedFiles((current) => ({ ...current, uploadProgress: { ...current.uploadProgress, overallStatus: UploadProgressOverallStatus.COMPLETE } }));
    };

    /** Callback function that is executed just before the file upload loop starts. */
    const onOverallUploadStart = (unusedFileManagementAreaId: string, filesToUploadCount: number) => {
        setManagedFiles((current) => ({ ...current, uploadProgress: { ...current.uploadProgress, filesToUploadCount: filesToUploadCount, overallStatus: UploadProgressOverallStatus.IN_PROGRESS } }));
    };

    const submitRequestWithManagedFiles = async (submitApiRequest: (fileUpdates: FileUpdates) => Promise<SubmitApiRequestReturnType>) => {
        const { apiRequestReturnValue, newlyUploadedFilesByArea } = await submitRequestWithFiles(
            documentApi,
            new DefaultMap(() => {
                throw new Error();
            }, [[FILE_MANAGEMENT_AREA_DRAG_AND_DROP_INPUT_ID, managedFiles]]),
            (getFileUpdatesByFileManagementAreaId) => submitApiRequest(getFileUpdatesByFileManagementAreaId(FILE_MANAGEMENT_AREA_DRAG_AND_DROP_INPUT_ID)),
            _restriction,
            onIndividualUploadComplete,
            onIndividualUploadProgress,
            onOverallUploadComplete,
            onOverallUploadStart
        );

        const newExistingFiles = [...managedFiles.existingFiles, ...newlyUploadedFilesByArea.get(FILE_MANAGEMENT_AREA_DRAG_AND_DROP_INPUT_ID)!];
        setManagedFiles((current) => ({ existingFiles: newExistingFiles, existingFilesToDelete: [], newFiles: [], uploadProgress: current.uploadProgress }));
        setLastSavedExistingFiles(newExistingFiles);

        return apiRequestReturnValue;
    };

    /** Discards any unsaved changes, reverting the managed files to either: the set that existed as of the last successful save (or page load); or a new list of uploaded files, if provided. */
    const reset = (newExistingFiles?: UploadedFile[]) => {
        setManagedFiles({
            existingFiles: newExistingFiles ?? lastSavedExistingFiles,
            existingFilesToDelete: [],
            newFiles: [],
            uploadProgress: {
                currentFileUploadPercent: 0,
                filesToUploadCount: 0,
                fileUploadsCompleteCount: 0,
                overallStatus: UploadProgressOverallStatus.NOT_STARTED,
            },
        });
    };

    const hasUnsavedChanges = managedFiles.existingFilesToDelete.length > 0 || managedFiles.newFiles.length > 0;

    return [submitRequestWithManagedFiles, { existingFiles: managedFiles.existingFiles, newFiles: managedFiles.newFiles, onAddNewFiles, onRemoveNewFile, onRemoveExistingFile, restriction: _restriction, uploadProgress: managedFiles.uploadProgress }, hasUnsavedChanges, reset];
};

/**
 * Contains current file state for a `FileManagementAreaWithSiblings`.
 * You should not need to use anything from an object that conforms to this interface. Just pass the object directly to `FileManagementAreaWithSiblings`.
 */
export interface FileManagementAreaWithSiblingsHookValues {
    managedFilesForAllAreas: DefaultMap<string, ManagedFilesForArea>;
    onAddNewFiles: (fileManagementAreaId: string, files: File[]) => void;
    onRemoveNewFile: (fileManagementAreaId: string, file: File) => void;
    onRemoveExistingFile: (fileManagementAreaId: string, file: UploadedFile) => void;
    restriction: FileManagementAreaRestriction;
}

/**
 * Whether a file management area currently holds any files (either files already saved, or files that will be uploaded on save).
 */
export const fileManagementAreaWithSiblingsHasFiles = (hookValues: FileManagementAreaWithSiblingsHookValues, fileManagementAreaId: string): boolean => {
    const managedFiles = hookValues.managedFilesForAllAreas.get(fileManagementAreaId);
    return managedFiles.existingFiles.length > 0 || managedFiles.newFiles.length > 0;
};

/**
 * Provides logic for multiple `FileManagementAreaWithSiblings`s that manage independent sets of files associated with some entity (an IRQ with multiple sections, a third-party due diligence assessment's control with multiple questions, etc.).
 * NOTE: You usually don't need to specify `SubmitApiRequestReturnType` (if you don't define it, it will default to `unknown`). Define it when the request for submitting a form returns a response that you need to make use of.
 *
 * @param documentApi The API used to upload files to S3 when the form is submitted.
 * @param existingFilesDefault The files currently associated with the entity at page load time.
 * @param restriction The restriction on the number of files that can be associated with the entity. Defaults to `FileManagementAreaRestriction.NO_RESTRICTION`. The restriction applies to each file area. For example, if the restriction is `FileManagementAreaRestriction.EXACTLY_ONE_FILE`, each area must contain exactly one file.
 * @returns Values to be passed directly into each corresponding `FileManagementAreaWithSiblings`, as well as a function that handles the "S3" dance when it comes time to submit the form and a boolean indicating whether there are any unsaved changes. The boolean is intended to be used in conjunction with `useUnsavedChangesTracking`, if a form needs it.
 */
export const useFileManagementAreas = <SubmitApiRequestReturnType,>(documentApi: DocumentApi, existingFilesDefault: Map<string, UploadedFile[]>, restriction?: FileManagementAreaRestriction): readonly [(submitApiRequest: (getFileUpdatesByFileManagementAreaId: (fileManagementAreaId: string) => FileUpdates) => Promise<SubmitApiRequestReturnType>) => Promise<SubmitApiRequestReturnType>, FileManagementAreaWithSiblingsHookValues, boolean] => {
    const [managedFilesForAllAreas, setManagedFilesForAllAreas] = useState<DefaultMap<string, ManagedFilesForArea>>(
        new DefaultMap(
            () => ({
                existingFiles: [],
                existingFilesToDelete: [],
                newFiles: [],
                uploadProgress: {
                    currentFileUploadPercent: 0,
                    filesToUploadCount: 0,
                    fileUploadsCompleteCount: 0,
                    overallStatus: UploadProgressOverallStatus.NOT_STARTED,
                },
            }),
            [...existingFilesDefault.entries()].map(([fileAreaId, existingFiles]) => [
                fileAreaId,
                {
                    existingFiles,
                    existingFilesToDelete: [],
                    newFiles: [],
                    uploadProgress: {
                        currentFileUploadPercent: 0,
                        filesToUploadCount: 0,
                        fileUploadsCompleteCount: 0,
                        overallStatus: UploadProgressOverallStatus.NOT_STARTED,
                    },
                },
            ])
        )
    );

    const _restriction = restriction ?? FileManagementAreaRestriction.NO_RESTRICTION;

    const onAddNewFiles = (fileManagementAreaId: string, newFiles: File[]) => {
        setManagedFilesForAllAreas((current) => {
            const newManagedFilesForAllAreas = current.copy();
            const currentManagedFilesForArea = newManagedFilesForAllAreas.get(fileManagementAreaId);
            const currentFileNames = [...currentManagedFilesForArea.newFiles.map((file) => file.name), ...currentManagedFilesForArea.existingFiles.map((file) => file.filename)];

            const validNewFiles = newFiles.filter((file) => !currentFileNames.includes(file.name));
            const sortedValidFiles = [...validNewFiles, ...currentManagedFilesForArea.newFiles].sort((a, b) => {
                return a.name.localeCompare(b.name);
            });

            newManagedFilesForAllAreas.set(fileManagementAreaId, { ...currentManagedFilesForArea, newFiles: sortedValidFiles });
            return newManagedFilesForAllAreas;
        });
    };

    const onRemoveNewFile = (fileManagementAreaId: string, fileToRemove: File) => {
        setManagedFilesForAllAreas((current) => {
            const newManagedFilesForAllAreas = current.copy();
            const currentManagedFilesForArea = newManagedFilesForAllAreas.get(fileManagementAreaId);
            const filteredFiles = currentManagedFilesForArea.newFiles.filter((file) => file.name !== fileToRemove.name);
            newManagedFilesForAllAreas.set(fileManagementAreaId, { ...currentManagedFilesForArea, newFiles: filteredFiles });
            return newManagedFilesForAllAreas;
        });
    };

    const onRemoveExistingFile = (fileManagementAreaId: string, fileToRemove: UploadedFile) => {
        setManagedFilesForAllAreas((current) => {
            const newManagedFilesForAllAreas = current.copy();
            const currentManagedFilesForArea = newManagedFilesForAllAreas.get(fileManagementAreaId);
            const filteredFiles = currentManagedFilesForArea.existingFiles.filter((file) => file.file_id !== fileToRemove.file_id);
            const existingFilesToDelete = [...currentManagedFilesForArea.existingFilesToDelete, fileToRemove.file_id];
            newManagedFilesForAllAreas.set(fileManagementAreaId, { ...currentManagedFilesForArea, existingFiles: filteredFiles, existingFilesToDelete });
            return newManagedFilesForAllAreas;
        });
    };

    /** Callback function that is executed when a file upload is complete. */
    const onIndividualUploadComplete = (fileManagementAreaId: string, fileUploadsCompleteCount: number) => {
        setManagedFilesForAllAreas((current) => {
            const newManagedFilesForAllAreas = current.copy();
            const currentManagedFilesForArea = newManagedFilesForAllAreas.get(fileManagementAreaId);
            // Setting currentFileUploadPercent to 0 when a file upload completes ensures the upload progress bar in the UI doesn't jump around.
            newManagedFilesForAllAreas.set(fileManagementAreaId, { ...currentManagedFilesForArea, uploadProgress: { ...currentManagedFilesForArea.uploadProgress, currentFileUploadPercent: 0, fileUploadsCompleteCount: fileUploadsCompleteCount } });
            return newManagedFilesForAllAreas;
        });
    };

    /** Callback function that is executed as a file is being uploaded. */
    const onIndividualUploadProgress = (fileManagementAreaId: string, progressEvent: AxiosProgressEvent) => {
        if (progressEvent.total) {
            const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setManagedFilesForAllAreas((current) => {
                const newManagedFilesForAllAreas = current.copy();
                const currentManagedFilesForArea = newManagedFilesForAllAreas.get(fileManagementAreaId);
                newManagedFilesForAllAreas.set(fileManagementAreaId, { ...currentManagedFilesForArea, uploadProgress: { ...currentManagedFilesForArea.uploadProgress, currentFileUploadPercent: percentComplete } });
                return newManagedFilesForAllAreas;
            });
        }
    };

    /** Callback function that is executed once all file uploads are complete. */
    const onOverallUploadComplete = (fileManagementAreaId: string) => {
        setManagedFilesForAllAreas((current) => {
            const newManagedFilesForAllAreas = current.copy();
            const currentManagedFilesForArea = newManagedFilesForAllAreas.get(fileManagementAreaId);
            newManagedFilesForAllAreas.set(fileManagementAreaId, { ...currentManagedFilesForArea, uploadProgress: { ...currentManagedFilesForArea.uploadProgress, overallStatus: UploadProgressOverallStatus.COMPLETE } });
            return newManagedFilesForAllAreas;
        });
    };

    /** Callback function that is executed just before the file upload loop starts. */
    const onOverallUploadStart = (fileManagementAreaId: string, filesToUploadCount: number) => {
        setManagedFilesForAllAreas((current) => {
            const newManagedFilesForAllAreas = current.copy();
            const currentManagedFilesForArea = newManagedFilesForAllAreas.get(fileManagementAreaId);
            newManagedFilesForAllAreas.set(fileManagementAreaId, { ...currentManagedFilesForArea, uploadProgress: { ...currentManagedFilesForArea.uploadProgress, filesToUploadCount: filesToUploadCount, overallStatus: UploadProgressOverallStatus.IN_PROGRESS } });
            return newManagedFilesForAllAreas;
        });
    };

    const submitRequestWithManagedFiles = async (submitApiRequest: (getFileUpdatesByFileManagementAreaId: (fileManagementAreaId: string) => FileUpdates) => Promise<SubmitApiRequestReturnType>) => {
        const { apiRequestReturnValue, newlyUploadedFilesByArea } = await submitRequestWithFiles(documentApi, managedFilesForAllAreas, submitApiRequest, _restriction, onIndividualUploadComplete, onIndividualUploadProgress, onOverallUploadComplete, onOverallUploadStart);

        setManagedFilesForAllAreas((current) => {
            return new DefaultMap(
                current.generateDefaultValue,
                [...current.entries()].map(([fileManagementAreaId, managedFilesForArea]) => {
                    const newExistingFiles = [...managedFilesForArea.existingFiles, ...newlyUploadedFilesByArea.get(fileManagementAreaId)!];
                    return [fileManagementAreaId, { existingFiles: newExistingFiles, existingFilesToDelete: [], newFiles: [], uploadProgress: managedFilesForArea.uploadProgress }];
                })
            );
        });

        return apiRequestReturnValue;
    };

    const hasUnsavedChanges = [...managedFilesForAllAreas.values()].some((managedFiles) => managedFiles.existingFilesToDelete.length > 0 || managedFiles.newFiles.length > 0);

    return [submitRequestWithManagedFiles, { managedFilesForAllAreas: managedFilesForAllAreas, onAddNewFiles, onRemoveNewFile, onRemoveExistingFile, restriction: _restriction }, hasUnsavedChanges];
};

interface FileManagementAreaProps {
    /**
     * If true, the user will not be able to interact with the area except to download existing files.
     * NOTE: If the entire enclosing form is disabled (for example, via `fieldset`), the minor bug of being unable to download existing files during the disabled state will occur. We could improve this by implementing our own form component that automatically handles tricky details like disabling some, but not all, form interactions at the proper times.
     */
    disabled: boolean;
    /**
     * Used to download existing files.
     */
    documentApi: DocumentApi;
    /**
     * Values obtained directly from the corresponding `useFileManagementArea` hook.
     */
    fileManagementHookValues: FileManagementAreaHookValues;

    /**
     * The label for the table of existing files. If not provided, "Existing File" or "Existing Files" will be used, based on whether `singleFileMode` is used.
     */
    existingFilesFormFieldLabel?: string;
    /**
     * The label for the file drag-and-drop input element. If not provided, "File" or "Files" will be used, based on whether `singleFileMode` is used.
     */
    fileDragAndDropFormFieldLabel?: string;
    /**
     * A tooltip that will be displayed above the file drag-and-drop input, horizontally aligned with the form field label.
     */
    tooltip?: string;
}

/**
 * Renders a group of components that manage files associated with some entity (an issue, exception, etc.).
 * **Must** be used with the `useFileManagementArea` hook.
 */
export const FileManagementArea = (props: FileManagementAreaProps) => {
    const fileManagementAreaProps: InternalFileManagementAreaProps = {
        disabled: props.disabled,
        documentApi: props.documentApi,
        existingFiles: props.fileManagementHookValues.existingFiles,
        onAddNewFiles: props.fileManagementHookValues.onAddNewFiles,
        onRemoveNewFile: props.fileManagementHookValues.onRemoveNewFile,
        onRemoveExistingFile: props.fileManagementHookValues.onRemoveExistingFile,
        uploadProgress: props.fileManagementHookValues.uploadProgress,
        newFiles: props.fileManagementHookValues.newFiles,
        existingFilesFormFieldLabel: props.existingFilesFormFieldLabel,
        inputId: FILE_MANAGEMENT_AREA_DRAG_AND_DROP_INPUT_ID,
        fileDragAndDropFormFieldLabel: props.fileDragAndDropFormFieldLabel,
        singleFileMode: props.fileManagementHookValues.restriction === FileManagementAreaRestriction.EXACTLY_ONE_FILE || props.fileManagementHookValues.restriction === FileManagementAreaRestriction.UP_TO_ONE_FILE,
        tooltip: props.tooltip,
    };

    return <InternalFileManagementArea {...fileManagementAreaProps} />;
};

interface FileManagementAreaWithSiblingsProps {
    /**
     * If true, the user will not be able to interact with the area except to download existing files.
     * NOTE: If the entire enclosing form is disabled (for example, via `fieldset`), the minor bug of being unable to download existing files during the disabled state will occur. We could improve this by implementing our own form component that automatically handles tricky details like disabling some, but not all, form interactions at the proper times.
     */
    disabled: boolean;
    /**
     * Used to download existing files.
     */
    documentApi: DocumentApi;
    /**
     * Values obtained directly from the corresponding `useFileManagementAreas` hook.
     */
    fileManagementHookValues: FileManagementAreaWithSiblingsHookValues;
    /**
     * An ID that is unique among all sibling file management areas on the page. This MUST BE a valid HTML identifier (so no whitespace, or you'll encounter bizarre bugs), and each identifier MUST remain the same throughout the lifetime of its corresponding file management area. If the file management areas are not dynamic, this can simply be a numerical index converted to a string.
     */
    uniqueId: string;

    /**
     * The label for the table of existing files. If not provided, "Existing File" or "Existing Files" will be used, based on whether `singleFileMode` is used.
     */
    existingFilesFormFieldLabel?: string;
    /**
     * The label for the file drag-and-drop input element. If not provided, "File" or "Files" will be used, based on whether `singleFileMode` is used.
     */
    fileDragAndDropFormFieldLabel?: string;
    /**
     * A tooltip that will be displayed above the file drag-and-drop input, horizontally aligned with the form field label.
     */
    tooltip?: string;
}

/**
 * Renders a group of components that manage one of many independent sets of files associated with some entity (an IRQ with multiple sections, a third-party due diligence assessment's control with multiple questions, etc.).
 * **Must** be used with the `useFileManagementAreas` hook.
 */
export const FileManagementAreaWithSiblings = (props: FileManagementAreaWithSiblingsProps) => {
    const fileManagementAreaProps: InternalFileManagementAreaProps = {
        disabled: props.disabled,
        documentApi: props.documentApi,
        existingFiles: props.fileManagementHookValues.managedFilesForAllAreas.get(props.uniqueId).existingFiles,
        onAddNewFiles: (files) => props.fileManagementHookValues.onAddNewFiles(props.uniqueId, files),
        onRemoveNewFile: (file) => props.fileManagementHookValues.onRemoveNewFile(props.uniqueId, file),
        onRemoveExistingFile: (file) => props.fileManagementHookValues.onRemoveExistingFile(props.uniqueId, file),
        uploadProgress: props.fileManagementHookValues.managedFilesForAllAreas.get(props.uniqueId).uploadProgress,
        newFiles: props.fileManagementHookValues.managedFilesForAllAreas.get(props.uniqueId).newFiles,
        existingFilesFormFieldLabel: props.existingFilesFormFieldLabel,
        inputId: props.uniqueId,
        fileDragAndDropFormFieldLabel: props.fileDragAndDropFormFieldLabel,
        singleFileMode: props.fileManagementHookValues.restriction === FileManagementAreaRestriction.EXACTLY_ONE_FILE || props.fileManagementHookValues.restriction === FileManagementAreaRestriction.UP_TO_ONE_FILE,
        tooltip: props.tooltip,
    };

    return <InternalFileManagementArea {...fileManagementAreaProps} />;
};

interface InternalFileManagementAreaProps {
    disabled: boolean;
    documentApi: DocumentApi;
    existingFiles: UploadedFile[];
    inputId: string;
    onAddNewFiles: (files: File[]) => void;
    onRemoveNewFile: (file: File) => void;
    onRemoveExistingFile: (file: UploadedFile) => void;
    uploadProgress: UploadProgress;
    newFiles: File[];
    singleFileMode: boolean;
    tooltip?: string;

    existingFilesFormFieldLabel?: string;
    fileDragAndDropFormFieldLabel?: string;
}

/**
 * Renders a file management area. This component is internal to this file and is not used directly: `FileManagementArea` and `FileManagementAreaWithSiblings` are exported to be used directly.
 * The point of this additional component is to DRY common code between the two versions of the file management area.
 */
const InternalFileManagementArea = (props: InternalFileManagementAreaProps) => {
    const defaultFileDragAndDropLabelText = props.singleFileMode ? DEFAULT_LABEL_FOR_FILE_DRAG_AND_DROP_SINGLE_MODE : DEFAULT_LABEL_FOR_FILE_DRAG_AND_DROP_MULTIPLE_MODE;
    const defaultExistingFilesLabelText = props.singleFileMode ? DEFAULT_LABEL_FOR_EXISTING_FILES_SINGLE_MODE : DEFAULT_LABEL_FOR_EXISTING_FILES_MULTIPLE_MODE;

    const fileDragAndDropLabelText = props.fileDragAndDropFormFieldLabel ?? defaultFileDragAndDropLabelText;
    const existingFilesLabelText = props.existingFilesFormFieldLabel ?? defaultExistingFilesLabelText;

    const fileDragAndDrop = (() => {
        if (props.singleFileMode) {
            const fileDragAndDropSingleModeProps: FileDragAndDropSingleModeProps = {
                labelText: fileDragAndDropLabelText,
                onSelectFile: (file) => {
                    if (props.newFiles.length > 0) {
                        props.onRemoveNewFile(props.newFiles[0]);
                    }

                    if (props.existingFiles.length > 0) {
                        props.onRemoveExistingFile(props.existingFiles[0]);
                    }

                    props.onAddNewFiles([file]);
                },
                onRemoveFile: () => props.onRemoveNewFile(props.newFiles[0]),
                uploadProgress: props.uploadProgress,
                file: props.newFiles[0],
                disabled: props.disabled,
                inputId: props.inputId,
                tooltip: props.tooltip,
            };
            return <FileDragAndDropSingleMode {...fileDragAndDropSingleModeProps} />;
        } else {
            const fileDragAndDropProps: FileDragAndDropProps = {
                labelText: fileDragAndDropLabelText,
                onAddFiles: props.onAddNewFiles,
                onRemoveFile: props.onRemoveNewFile,
                uploadProgress: props.uploadProgress,
                files: props.newFiles,
                disabled: props.disabled,
                inputId: props.inputId,
                tooltip: props.tooltip,
            };
            return <FileDragAndDrop {...fileDragAndDropProps} />;
        }
    })();

    return (
        <>
            {fileDragAndDrop}
            {/* TODO: Consider updating the file drag-and-drop to show both new and existing files within the dashed rectangle, if possible. I find the UI with two distinct sections and different ways of interacting with the files to be clunky. IMPORTANT: Some forms, such as Create Risk Review, rely on the fact that "existing files" is not shown, as it would not make sense; keep this in mind if/when the UI is changed. */}
            {props.existingFiles.length > 0 && (
                <>
                    <VisualLabel>{existingFilesLabelText}</VisualLabel>
                    <MUITable>
                        <MUITableBody>
                            {[...props.existingFiles]
                                .sort((fileA, fileB) => fileA.filename.localeCompare(fileB.filename))
                                .map((file) => {
                                    const overflowItems: OverflowItem[] = [];

                                    if (file.file_state === FileState.PASSED) {
                                        overflowItems.push({
                                            text: 'Download file',
                                            onClickAction: () => downloadDocument(props.documentApi, file),
                                            icon: ICON_DOWNLOAD,
                                        });
                                    }

                                    // Do not allow deleting the file if it is still processing.
                                    // This prevents a back-end race condition where the file processing function raises an exception when it tries to update the database record for a file that has been deleted.
                                    if (!props.disabled && file.file_state !== FileState.PROCESSING) {
                                        overflowItems.push({
                                            text: 'Delete file',
                                            onClickAction: () => props.onRemoveExistingFile(file),
                                            icon: ICON_DELETE_REMOVE,
                                        });
                                    }

                                    return (
                                        <MUITableRow key={file.file_id}>
                                            <MUITableCell>
                                                <div className={styles.fileNameAndState}>
                                                    <Text noStyles>{file.filename}</Text>
                                                    <FileStateToolTip fileState={file.file_state} />
                                                </div>
                                            </MUITableCell>
                                            <MUITableOverflowCell>
                                                {overflowItems.length > 0 && (
                                                    <div className={styles.overflowContainer}>
                                                        <OverflowMenu overflowItems={overflowItems} accessibilityTitle={`Open menu for ${file.filename}`} />
                                                    </div>
                                                )}
                                            </MUITableOverflowCell>
                                        </MUITableRow>
                                    );
                                })}
                        </MUITableBody>
                    </MUITable>
                </>
            )}
        </>
    );
};

/**
 * Handles the "S3" dance when submitting a form. Intended to be used when the user presses the submit button for a form with one or more file management areas.
 * Handles validation of all files.
 */
const submitRequestWithFiles = async <SubmitApiRequestReturnType,>(documentApi: DocumentApi, managedFilesForAllAreas: DefaultMap<string, ManagedFilesForArea>, submitApiRequest: (getFileUpdatesByFileManagementAreaId: (fileManagementAreaId: string) => FileUpdates) => Promise<SubmitApiRequestReturnType>, restriction: FileManagementAreaRestriction, onIndividualUploadComplete: (fileManagementAreaId: string, fileUploadsComplete: number) => void, onIndividualUploadProgress: (fileManagementAreaId: string, progressEvent: AxiosProgressEvent) => void, onOverallUploadComplete: (fileManagementAreaId: string) => void, onOverallUploadStart: (fileManagementAreaId: string, fileUploadsCompleteCount: number) => void): Promise<{ apiRequestReturnValue: SubmitApiRequestReturnType; newlyUploadedFilesByArea: Map<string, UploadedFile[]> }> => {
    switch (restriction) {
        case FileManagementAreaRestriction.NO_RESTRICTION:
            break;
        case FileManagementAreaRestriction.EXACTLY_ONE_FILE:
            managedFilesForAllAreas.forEach((filesForArea) => {
                if (filesForArea.newFiles.length + filesForArea.existingFiles.length !== 1) {
                    // Note: this isn't a great error message for the user when multiple file management areas are on the screen, since it doesn't explain where the problem is. But, this is a temporary approach until we enhance file validation to occur at the time of updating files.
                    throw new Error('File is required.');
                }
            });
            break;
        case FileManagementAreaRestriction.UP_TO_ONE_FILE:
            managedFilesForAllAreas.forEach((filesForArea) => {
                if (filesForArea.newFiles.length + filesForArea.existingFiles.length > 1) {
                    // Note: this isn't a great error message for the user when multiple file management areas are on the screen, since it doesn't explain where the problem is. But, this is a temporary approach until we enhance file validation to occur at the time of updating files.
                    throw new Error('Only a single file is allowed.');
                }
            });
            break;
    }

    for (const managedFiles of managedFilesForAllAreas.values()) {
        for (const newFile of managedFiles.newFiles) {
            validateFileForUpload(newFile);
        }
    }

    const fileUpdatesByArea: Map<string, FileUpdates> = new Map([...managedFilesForAllAreas].map(([fileManagementAreaId, info]) => [fileManagementAreaId, { existing_files_to_delete: info.existingFilesToDelete, new_files: [] }]));

    const pendingDocumentUploadsByFileManagementAreaIndex: Map<string, { signedUpload: SignedUploadResponse; file: File }[]> = new Map();

    try {
        for (const [fileManagementAreaId, fileInfo] of managedFilesForAllAreas.entries()) {
            const pendingDocumentUploads: {
                signedUpload: SignedUploadResponse;
                file: File;
            }[] = [];

            for (const file of fileInfo.newFiles) {
                const signedUploadResponse = (await documentApi.getSignedUpload(file.name)).data;
                pendingDocumentUploads.push({ signedUpload: signedUploadResponse, file: file });
                fileUpdatesByArea.get(fileManagementAreaId)!.new_files.push({ filename: file.name, file_id: signedUploadResponse.file_id });
            }

            pendingDocumentUploadsByFileManagementAreaIndex.set(fileManagementAreaId, pendingDocumentUploads);
        }
    } catch (error) {
        console.log(error);
        throw new Error('Something went wrong when trying to upload files.');
    }

    const getFileUpdatesByFileManagementAreaId = (fileManagementAreaId: string): FileUpdates => fileUpdatesByArea.get(fileManagementAreaId) ?? { existing_files_to_delete: [], new_files: [] };
    const apiRequestReturnValue: SubmitApiRequestReturnType = await submitApiRequest(getFileUpdatesByFileManagementAreaId);

    const failedFileNames: string[] = [];
    // Upload each document to S3. If any error occurs, remember it for below, but continue trying to upload other documents.
    for (const [fileManagementAreaId, pendingDocumentUploads] of pendingDocumentUploadsByFileManagementAreaIndex.entries()) {
        if (pendingDocumentUploads.length === 0) {
            continue;
        }

        /** Callback function that is executed as a file is being uploaded. */
        const onIndividualUploadProgressAxios = (progressEvent: AxiosProgressEvent) => {
            return onIndividualUploadProgress(fileManagementAreaId, progressEvent);
        };

        onOverallUploadStart(fileManagementAreaId, pendingDocumentUploads.length);

        let fileUploadsComplete = 0;
        for (const pendingUpload of [...pendingDocumentUploads.values()].flat()) {
            try {
                await documentApi.uploadDocument(pendingUpload.signedUpload.url, pendingUpload.signedUpload.fields, pendingUpload.file, onIndividualUploadProgressAxios);
                onIndividualUploadComplete(fileManagementAreaId, ++fileUploadsComplete);
            } catch (error) {
                console.log(error);
                failedFileNames.push(pendingUpload.file.name);
            }
        }

        onOverallUploadComplete(fileManagementAreaId);
    }

    if (failedFileNames.length > 0) {
        throw new Error(`Something went wrong when trying to upload: ${failedFileNames.join(', ')}.`);
    }

    const newlyUploadedFilesByArea: Map<string, UploadedFile[]> = new Map([...fileUpdatesByArea].map(([fileManagementAreaId, fileUpdates]) => [fileManagementAreaId, fileUpdates.new_files.map((file) => ({ ...file, created_timestamp: jsDateToIso8601(new Date()), file_state: FileState.PROCESSING }))]));

    return { apiRequestReturnValue, newlyUploadedFilesByArea };
};

interface BaseFileDragAndDropDropzoneProps {
    inputId: string;
    labelText: string;
    labelColor?: Colors;
    required?: boolean;
    tooltip?: string;
    disabled?: boolean;
}

interface FileDragAndDropDropzoneProps extends BaseFileDragAndDropDropzoneProps {
    onAddFiles: (files: File[]) => void;
}

interface FileDragAndDropDropzoneSingleModeProps extends BaseFileDragAndDropDropzoneProps {
    onSelectFile: (file: File) => void;
}

const FileDragAndDropDropzone = ({ disabled = false, ...props }: FileDragAndDropDropzoneProps & { singleFileMode: boolean; hasSelectedFiles: boolean }): JSX.Element => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: props.onAddFiles, disabled: disabled, multiple: props.singleFileMode !== true });
    const dragClassName = isDragActive ? styles.dropzoneDragActive : '';
    const disabledClassName = disabled ? styles.disabled : '';

    const promptText = (() => {
        if (props.singleFileMode) {
            if (props.hasSelectedFiles) {
                return 'Drag and drop a file, or click here to select a file. Selecting a new file will replace the current file.';
            } else {
                return 'Drag and drop a file, or click here to select a file. Only a single file can be selected.';
            }
        } else {
            return 'Drag and drop files, or click here to select files.';
        }
    })();

    return (
        <>
            <label htmlFor={props.inputId}>
                <VisualLabel color={props.labelColor} required={props.required} tooltip={props.tooltip}>
                    {props.labelText}
                </VisualLabel>
            </label>
            <div {...getRootProps({ className: `${styles.dropzone} ${dragClassName} ${disabledClassName}` })}>
                {/* Properties explicitly defined are not passed through by react-dropzone */}
                <input id={props.inputId} disabled={disabled} {...getInputProps()} />
                <Text color="darkGray">{promptText}</Text>
            </div>
        </>
    );
};

interface BaseDropzoneTableProps {
    hideRemoveNewDocumentButton?: boolean;
}

interface DropzoneTableProps extends BaseDropzoneTableProps {
    files: File[];
    onRemoveFile: (file: File) => void;
    uploadProgress: UploadProgress;
}

interface DropzoneTableSingleModeProps extends BaseDropzoneTableProps {
    file?: File;
    onRemoveFile: () => void;
    uploadProgress: UploadProgress;
}

const FileDragAndDropTable = ({ hideRemoveNewDocumentButton = false, ...props }: DropzoneTableProps): JSX.Element => {
    /** Calculate the total upload progress of all files as a percent. */
    const calculateTotalUploadProgressPercent = (): number => {
        if (props.uploadProgress.filesToUploadCount === 0) {
            return 0;
        }
        return Math.round(((props.uploadProgress.fileUploadsCompleteCount * 100 + props.uploadProgress.currentFileUploadPercent) / (props.uploadProgress.filesToUploadCount * 100)) * 100);
    };

    if (props.files.length === 0) {
        if (props.uploadProgress.overallStatus === UploadProgressOverallStatus.COMPLETE) {
            return (
                <div className={styles.uploadProgressContainer}>
                    <div className={styles.uploadProgressText}>
                        <Text noStyles>{`Upload complete ${props.uploadProgress.fileUploadsCompleteCount} of ${props.uploadProgress.filesToUploadCount}: `}</Text>
                    </div>
                    <div className={styles.uploadProgressBar}>
                        <ProgressBarIndicator size="large" percent={100} variant={IndicatorVariant.DARK_GREEN} />
                    </div>
                </div>
            );
        } else return <></>;
    }

    return (
        <>
            {props.uploadProgress.overallStatus === UploadProgressOverallStatus.IN_PROGRESS && (
                <>
                    <Text color="red">Wait for file upload(s) to complete.</Text>
                    <div className={styles.uploadProgressContainer}>
                        <div className={styles.uploadProgressText}>
                            <Text noStyles>{`Uploading ${props.uploadProgress.fileUploadsCompleteCount + 1} of ${props.uploadProgress.filesToUploadCount}: `}</Text>
                        </div>
                        <div className={styles.uploadProgressBar}>
                            <ProgressBarIndicator size="large" percent={calculateTotalUploadProgressPercent()} variant={IndicatorVariant.DARK_GREEN} />
                        </div>
                    </div>
                </>
            )}
            <MUITable>
                <MUITableBody>
                    {props.files.map((file, index) => {
                        return (
                            <MUITableRow key={index}>
                                <MUITableCell>
                                    <Text noStyles>{file.name}</Text>
                                </MUITableCell>
                                {!hideRemoveNewDocumentButton && (
                                    <MUITableOverflowCell>
                                        <div className={styles.overflowContainer}>
                                            <ListItemIcon
                                                sx={{
                                                    color: 'var(--hps-gray)',
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    minWidth: 0,
                                                    padding: '12px',
                                                }}
                                            >
                                                <IconButton aria-label={`Remove ${file.name}`} onClick={() => props.onRemoveFile(file)} fontAwesomeImage={ICON_DELETE_REMOVE} />
                                            </ListItemIcon>
                                        </div>
                                    </MUITableOverflowCell>
                                )}
                            </MUITableRow>
                        );
                    })}
                </MUITableBody>
            </MUITable>
        </>
    );
};

type FileDragAndDropProps = FileDragAndDropDropzoneProps & DropzoneTableProps;
type FileDragAndDropSingleModeProps = FileDragAndDropDropzoneSingleModeProps & DropzoneTableSingleModeProps;

/**
 * Renders an input that allows users to drag and drop files, or click to select files.
 */
const FileDragAndDrop = (props: FileDragAndDropProps): JSX.Element => {
    const dropzoneProps = { singleFileMode: false, hasSelectedFiles: props.files.length > 0, ...props };

    return (
        <>
            <FileDragAndDropDropzone {...dropzoneProps} />
            <FileDragAndDropTable {...props} />
        </>
    );
};

/**
 * Renders/functions the same as `FileDragAndDrop`, but allows only a single file to be selected (if a file has been selected, selecting a second file will replace the first file).
 */
const FileDragAndDropSingleMode = (props: FileDragAndDropSingleModeProps): JSX.Element => {
    const dropzoneProps = { singleFileMode: true, hasSelectedFiles: props.file !== undefined, onAddFiles: (files: File[]) => props.onSelectFile(files[0]), ...props };
    const dropzoneTableProps = { files: props.file ? [props.file] : [], ...props };

    return (
        <>
            <FileDragAndDropDropzone {...dropzoneProps} />
            <FileDragAndDropTable {...dropzoneTableProps} />
        </>
    );
};

/**
 * Supports the ability to share files among answers/controls for third-party due diligence questionnaires and third-party due diligence assessments.
 * TODO: Ideally, if possible, this function should be consolidated with `submitRequestWithFiles`. Differences between the two:
 * * This function only handles new files to upload, since the "sharing among multiple entities" logic needs to be handled by the user of this function (i.e., third-party due diligence questionnaire and third-party due diligence assessment components).
 * * This function de-duplicates files with the same name, which is probably something the rest of the app should have always been doing.
 */
export const submitApiRequestWithNewFiles = async <T,>(documentApi: DocumentApi, pendingFilesByIdentifier: Map<string, File[]>, submitApiRequest: (getFilesToBeUploaded: (identifier: string) => FileToBeUploaded[]) => Promise<T>): Promise<T> => {
    // De-duplicate new `File`s by file name.
    const uniqueNewFilesByName: Map<string, File> = new Map();
    for (const newFiles of pendingFilesByIdentifier.values()) {
        for (const file of newFiles) {
            validateFileForUpload(file);
            if (!uniqueNewFilesByName.has(file.name)) {
                uniqueNewFilesByName.set(file.name, file);
            }
        }
    }

    // Get signed upload URLs for each unique new file and build a lookup map.
    const signedUploadsByFileName: Map<string, { signedUpload: SignedUploadResponse; file: File }> = new Map();
    try {
        for (const [fileName, file] of uniqueNewFilesByName.entries()) {
            const signedUploadResponse = (await documentApi.getSignedUpload(fileName)).data;
            signedUploadsByFileName.set(fileName, { signedUpload: signedUploadResponse, file });
        }
    } catch (error) {
        console.log(error);
        throw new Error('Something went wrong when trying to upload files.');
    }

    // Build the file changes for each identifier.
    const filesToBeUploadedByIdentifier: Map<string, FileToBeUploaded[]> = new Map();
    for (const [identifier, newFiles] of pendingFilesByIdentifier.entries()) {
        const filesToBeUploaded: FileToBeUploaded[] = newFiles.map((file) => {
            const signedUploadInfo = signedUploadsByFileName.get(file.name)!;
            return { filename: file.name, file_id: signedUploadInfo.signedUpload.file_id };
        });

        filesToBeUploadedByIdentifier.set(identifier, filesToBeUploaded);
    }

    const getFilesToBeUploaded = (identifier: string): FileToBeUploaded[] => filesToBeUploadedByIdentifier.get(identifier) ?? [];

    const apiRequestReturnValue = await submitApiRequest(getFilesToBeUploaded);

    // Upload each new file to S3.
    const failedFileNames: string[] = [];
    for (const { signedUpload, file } of signedUploadsByFileName.values()) {
        try {
            await documentApi.uploadDocument(signedUpload.url, signedUpload.fields, file, () => {});
        } catch (error) {
            console.log(error);
            failedFileNames.push(file.name);
        }
    }

    if (failedFileNames.length > 0) {
        throw new Error(`Something went wrong when trying to upload: ${failedFileNames.join(', ')}.`);
    }

    return apiRequestReturnValue;
};
